/**
 * Signature-field placement on an uploaded offer PDF (AXHE-4085).
 *
 * An uploaded override (AXHE-3720) is a flat PDF with no `[SIGNATURE]` text
 * tags, so nothing tells Dropbox Sign where the signature boxes go. The
 * recruiter draws them here and the monolith turns them into explicit field
 * coordinates (`OfferLetters::FormFieldsBuilder`).
 */

/** Field kinds the backend can map onto a Dropbox Sign form field. */
export type PlacementFieldType =
  | 'signature'
  | 'initials'
  | 'date'
  | 'text'
  | 'checkbox';

/** Palette order — signature first, since it is what every offer needs. */
export const PLACEMENT_FIELD_TYPES: PlacementFieldType[] = [
  'signature',
  'initials',
  'date',
  'text',
  'checkbox',
];

/**
 * One placement, exactly as it is persisted and handed to the backend.
 *
 * `x`/`y`/`width`/`height` are **integer 72 DPI PDF points with the origin at
 * the top-left of the page and y measured downward**. They are NOT screen
 * pixels and NOT 80 DPI: the backend rescales size to Dropbox Sign's 80 DPI
 * itself (`FormFieldsBuilder::SIZE_SCALE`), so anything pre-scaled here is
 * scaled twice. See `geometry.ts` for the conversion out of the rendered view.
 */
export interface SignatureFieldPlacement {
  type: PlacementFieldType;
  /** Index into the offer's send-time signer list; the candidate is 0. */
  signer: number;
  /** 1-based page number. */
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
}

/**
 * A placement while it is being edited. `id` is client-only — React needs a
 * stable key that survives moves and reorders, and array index does not.
 */
export interface DraftPlacement extends SignatureFieldPlacement {
  id: string;
}

/** A PDF page's intrinsic size, in 72 DPI points. */
export interface PageGeometry {
  widthPt: number;
  heightPt: number;
}

/** A rectangle in PDF points (top-left origin). */
export interface RectPt {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A rectangle in rendered CSS pixels, relative to the page's top-left. */
export interface RectPx {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A point in rendered CSS pixels, relative to the page's top-left. */
export interface PointPx {
  x: number;
  y: number;
}
